import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import "./OrderDetailsModal.css";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import { styled } from '@mui/material/styles';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';

const OrderDetailsModal = (props) => {
  const [restaurantName, setRestaurantName] = useState([]);
  const { userCartData, totalServiceCharge, deliveryCharge, serviceChargeEnabledByRestaurant, handleServiceChargeToggle: parentHandleServiceChargeToggle } = props;
  const [transformedData, setTransformedData] = useState([]);
  const [exclusivePrice, setExclusivePrice] = useState(0);
  const [reloadIndication, setReloadIndication] = useState("");
  // Use parent's service charge state if provided, otherwise use local state
  const [localIsServiceChargeEnabledByRestaurant, setLocalIsServiceChargeEnabledByRestaurant] = useState({});
  const [serviceChargeVat, setServiceChargeVat] = useState(0);
  // If multiple restaurants are present, modal is opened from Checkout
  const isMultiRestaurant = (userCartData?.length || 0) > 1;

  // Styled switch component
  const AntSwitch = styled(Switch)(({ theme }) => ({
    width: 28,
    height: 16,
    padding: 0,
    display: 'flex',
    '&:active': {
      '& .MuiSwitch-thumb': {
        width: 15,
      },
      '& .MuiSwitch-switchBase.Mui-checked': {
        transform: 'translateX(9px)',
      },
    },
    '& .MuiSwitch-switchBase': {
      padding: 2,
      '&.Mui-checked': {
        transform: 'translateX(12px)',
        color: '#fff',
        '& + .MuiSwitch-track': {
          opacity: 1,
          backgroundColor: '#1890ff',
          ...theme.applyStyles('dark', {
            backgroundColor: '#177ddc',
          }),
        },
      },
    },
    '& .MuiSwitch-thumb': {
      boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)',
      width: 12,
      height: 12,
      borderRadius: 6,
      transition: theme.transitions.create(['width'], {
        duration: 200,
      }),
    },
    '& .MuiSwitch-track': {
      borderRadius: 16 / 2,
      opacity: 1,
      backgroundColor: 'rgba(0,0,0,.25)',
      boxSizing: 'border-box',
      ...theme.applyStyles('dark', {
        backgroundColor: 'rgba(255,255,255,.35)',
      }),
    },
  }));

  // Initialize service charge toggle based on isServiceChargeCom
  useEffect(() => {
    if (userCartData && userCartData.length > 0) {
      setLocalIsServiceChargeEnabledByRestaurant((prev) => {
        const next = { ...prev };
        userCartData.forEach((cart) => {
          if (next[cart.cartRestaurantId] === undefined) next[cart.cartRestaurantId] = true;
          if (cart.isServiceChargeCom === true) next[cart.cartRestaurantId] = true;
        });
        return next;
      });
    }
  }, [userCartData]);


  // Calculate service charge VAT when service charge changes

  useEffect(() => {
    if (userCartData && userCartData.length > 0) {
      userCartData.forEach(item => {
        setRestaurantName(prevValue => [...prevValue, item.restaurantName]);
      });
    }
  }, [userCartData]);

  const calculateExclusivePrice = useCallback(() => {
    let totalPrice = 0;

    for (let i = 0; i < userCartData.length; i++) {
      for (let j = 0; j < userCartData[i].subOrders.length; j++) {
        for (
          let k = 0;
          k < userCartData[i].subOrders[j].itemDetails.length;
          k++
        ) {
          const item = userCartData[i].subOrders[j].itemDetails[k];
          totalPrice += item.basePrice * item.foodItemQuantity;

          for (let l = 0; l < item.addOns.length; l++) {
            totalPrice +=
              item.addOns[l].addOnBasePrice * item.addOns[l].addOnQuantity;

            for (let m = 0; m < item.addOns[l].options.length; m++) {
              const option = item.addOns[l].options[m];
              totalPrice += option.optionBasePrice * option.optionQuantity;

              for (let n = 0; n < option.subOptions.length; n++) {
                totalPrice += option.subOptions[n].subOptionBasePrice;
              }
            }
          }
        }
      }
    }

    setExclusivePrice(totalPrice);
  }, [userCartData]);

  useEffect(() => {
    calculateExclusivePrice();
  }, [calculateExclusivePrice]);

  const transformCartData = (data) => {
    const transformed = [];

    data.forEach((cart) => {
      cart.subOrders.forEach((subOrder) => {
        subOrder.itemDetails?.forEach((item) => {
          const transformedItem = {
            cartItemId: item.cartItemId,
            foodItemId: item.foodItemId,
            foodItemName: item.foodItemName,
            foodItemImage: item.foodItemImage,
            basePrice: item.basePrice,
            vatInAmount: item.vatInAmount,
            sdInAmount: item.sdInAmount,
            discountInAmount: item.discountInAmount,
            totalPrice: item.totalPrice,
            kitchenId: item.kitchenId,
            kitchenName: item.kitchenName,
            foodItemQuantity: item.foodItemQuantity,
            credit: item.totalPrice >= cart.minimumOrderPriceToGiveCredit ? cart.creditLimitPerOrder : 0,
            vatPercentOnSd: 0,
            vatOnSd: 0,
            inclusiveSD: 0,
            addOns: item.addOns?.map((addOn) => ({
              cartAddOnId: addOn.cartAddOnId,
              addOnId: addOn.addOnId,
              addOnName: addOn.addOnName,
              addOnBasePrice: addOn.addOnBasePrice,
              addOnVatAmount: addOn.addOnVatAmount,
              addOnSdAmount: addOn.addOnSdAmount,
              addOnDiscountAmount: addOn.addOnDiscountAmount,
              addOnTotalPrice: addOn.addOnTotalPrice,
              addOnQuantity: addOn.addOnQuantity,
              options: addOn.options?.map((option) => ({
                cartOptionId: option.cartOptionId,
                optionId: option.optionId,
                optionName: option.optionName,
                optionBasePrice: option.optionBasePrice,
                optionVatAmount: option.optionVatAmount,
                optionSdAmount: option.optionSdAmount,
                optionDiscountAmount: option.optionDiscountAmount,
                optionTotalPrice: option.optionTotalPrice,
                optionQuantity: option.optionQuantity,
                optionVatPercentOnSd: 0,
                optionVatOnSd: 0,
                optionInclusiveSD: 0,
                subOptions: option.subOptions?.map((subOption) => ({
                  cartSubOptionId: subOption.cartSubOptionId,
                  subOptionId: subOption.subOptionId,
                  subOptionName: subOption.subOptionName,
                  subOptionBasePrice: subOption.subOptionBasePrice,
                  subOptionVatAmount: subOption.subOptionVatAmount,
                  subOptionSdAmount: subOption.subOptionSdAmount,
                  subOptionDiscountAmount: subOption.subOptionDiscountAmount,
                  subOptionTotalPrice: subOption.subOptionTotalPrice,
                  subOptionVatPercentOnSd: 0,
                  subOptionVatOnSd: 0,
                  subOptionInclusiveSD: 0,
                })),
              })),
            })),
          };

          transformed.push(transformedItem);
        });
      });
    });

    return transformed;
  };

  useEffect(() => {
    if (userCartData) {
      setTransformedData(transformCartData(userCartData));
    }
  }, [userCartData]);

  const getPercentageOrAmount = (amount1, amount2, mode) => {
    if (mode === "percentage") {
      var percentageAmount = (amount2 / amount1) * 100;
      return percentageAmount.toFixed(2); // amount2 is what percentage of amount1
    } else if (mode === "amount") {
      return (amount1 * amount2) / 100; // amount2% of amount1
    } else {
      return "Invalid mode";
    }
  };

  const getBasePrice = () => {
    let total = 0;
    userCartData?.forEach((cart) => {
      cart.subOrders.forEach((subOrder) => {
        subOrder.itemDetails.forEach((item) => {
          total += item.basePrice;
        });
      });
    });

    return total;
  };

  // const calculateSubTotal = () => {
  //     if (!userCartData) return 0;

  //     let total = 0;

  //     userCartData.forEach(data => {
  //         data.subOrders.forEach(subOrder => {
  //             subOrder.itemDetails.forEach(item => {
  //                 // Calculate base item price
  //                 const itemTotal = item.basePrice + item.vatInAmount + item.sdInAmount;
  //                 total += itemTotal * item.foodItemQuantity;

  //                 // Calculate add-ons
  //                 (item.addOns || []).forEach(addOn => {
  //                     // Calculate options for each add-on
  //                     (addOn.options || []).forEach(option => {
  //                         const optionTotal = option.optionBasePrice + option.optionVatAmount + option.optionSdAmount;
  //                         total += optionTotal * option.optionQuantity;

  //                         // Calculate sub-options for each option
  //                         (option.subOptions || []).forEach(subOption => {
  //                             const subOptionTotal = subOption.subOptionBasePrice + subOption.subOptionVatAmount + subOption.subOptionSdAmount;
  //                             total += subOptionTotal;
  //                         });
  //                     });
  //                 });
  //             });
  //         });
  //     });

  //     return total;
  // };

  const [subTotal, setSubTotal] = useState(0);
  const [totalSD, setTotalSD] = useState(0);
  const [totalVAT, setTotalVAT] = useState(0);
  const TotalTips = 0;
  // const deliveryCharge = 20;
  const serviceCharge = 0;

  /* Calculating Total of SubOrders */
  useEffect(() => {
    const calculateTotalBasePrice = () => {
      let subTotal = 0;
      let totalVat = 0;
      let totalSd = 0;
      userCartData?.forEach((data) => {
        data.subOrders?.forEach((subOrder) => {
          subOrder.itemDetails.forEach((item) => {
            subTotal += item.basePrice * item.foodItemQuantity;
            totalVat += item.vatInAmount * item.foodItemQuantity;
            totalSd += item.sdInAmount * item.foodItemQuantity;
            // Calculate add-ons
            (item.addOns || []).forEach((addOn) => {
              subTotal += addOn.addOnBasePrice * addOn.addOnQuantity;
              totalVat += addOn.addOnVatAmount * addOn.addOnQuantity;
              totalSd += addOn.addOnSdAmount * addOn.addOnQuantity;

              // Calculate options for each add-on
              (addOn.options || []).forEach((option) => {
                subTotal += option.optionBasePrice * option.optionQuantity;
                totalVat += option.optionVatAmount * option.optionQuantity;
                totalSd += option.optionSdAmount * option.optionQuantity;

                // Calculate sub-options for each option
                (option.subOptions || []).forEach((subOption) => {
                  subTotal += subOption.subOptionBasePrice;
                  totalVat += subOption.subOptionVatAmount;
                  totalSd += subOption.subOptionSdAmount;
                });
              });
            });
          });
        });
      });

      setTotalSD(totalSd);
      setTotalVAT(totalVat);
      setSubTotal(subTotal);
    };
    calculateTotalBasePrice();
  }, [userCartData]);

  const calCulateServiceCharge = useCallback(() => {
    let total = 0;
    userCartData.forEach((d) => {
      const rid = d.cartRestaurantId;
      const enabled = (serviceChargeEnabledByRestaurant && serviceChargeEnabledByRestaurant[rid] !== undefined)
        ? serviceChargeEnabledByRestaurant[rid]
        : (localIsServiceChargeEnabledByRestaurant[rid] ?? true);
      if (!enabled) return;
      let totalPrice = 0;
      d.subOrders.forEach((s) => {
        if (s.orderTypeName !== 'In-Store') return;
        s.itemDetails.forEach((i) => {
          totalPrice += i.basePrice * i.foodItemQuantity;
          i.addOns.forEach((a) => {
            totalPrice += a.addOnBasePrice * a.addOnQuantity;
            a.options.forEach((o) => {
              totalPrice += o.optionBasePrice * o.optionQuantity;
              o.subOptions.forEach((ss) => {
                const qty = ss.subOptionQuantity ?? 1;
                totalPrice += ss.subOptionBasePrice * qty;
              });
            });
          });
        });
      });
      total += (d.serviceCharge / 100) * totalPrice;
    });
    return total;
  }, [serviceChargeEnabledByRestaurant, localIsServiceChargeEnabledByRestaurant, userCartData]);

  useEffect(() => {
    const serviceChargeAmount = calCulateServiceCharge();
    const serviceChargeVatAmount = (serviceChargeAmount * 20) / 100;
    setServiceChargeVat(serviceChargeVatAmount);
  }, [calCulateServiceCharge]);

  const calculateTotal = () => {
    var serviceCharge = calCulateServiceCharge();
    var converteddeliveryCharge = deliveryCharge ? deliveryCharge : 0;
    var total =
      subTotal + totalSD + serviceChargeVat + totalVAT + converteddeliveryCharge + serviceCharge;
    return total.toFixed(2);
  };

  const calculateTotalCredit = () => {
    const creditEligibility = [];
    let totalCredit = 0;

    userCartData?.forEach((data) => {
      let subTotal = 0;

      data.subOrders?.forEach((subOrder) => {
        subOrder.itemDetails.forEach((item) => {

          subTotal += (item.basePrice * item.foodItemQuantity) + (item.vatInAmount * item.foodItemQuantity) + (item.sdInAmount * item.foodItemQuantity);

          (item.addOns || []).forEach((addOn) => {
            subTotal += (addOn.addOnBasePrice * addOn.addOnQuantity) + (addOn.addOnVatAmount * addOn.addOnQuantity) + (addOn.addOnSdAmount * addOn.addOnQuantity);

            (addOn.options || []).forEach((option) => {
              subTotal += (option.optionBasePrice * option.optionQuantity) + (option.optionVatAmount * option.optionQuantity) + (option.optionSdAmount * option.optionQuantity);

              (option.subOptions || []).forEach((subOption) => {
                subTotal += (subOption.subOptionBasePrice) + (subOption.subOptionVatAmount) + (subOption.subOptionSdAmount);
              });
            });
          });
        });
      });

      const isEligible = subTotal >= data.minimumOrderPriceToGiveCredit && data.restaurantCredit !== 0;

      if (isEligible) {
        totalCredit += data.creditLimitPerOrder;
      }

      creditEligibility.push({
        restaurantName: data.restaurantName,
        subTotal: subTotal.toFixed(2),
        eligibleForCredit: isEligible,
        creditLimitPerOrder: data.creditLimitPerOrder,
        restaurantCredit: data.restaurantCredit
      });
    });

    return totalCredit;
  }

  const handleServiceChargeToggle = useCallback((restaurantId) => {
    // Parent handler will toggle per-restaurant in parent; fallback to local map
    if (parentHandleServiceChargeToggle) {
      parentHandleServiceChargeToggle(restaurantId);
    } else {
      setLocalIsServiceChargeEnabledByRestaurant((prev) => ({
        ...prev,
        [restaurantId]: !(prev[restaurantId] ?? true),
      }));
    }
  }, [parentHandleServiceChargeToggle]);

  return (
    <Modal
      {...props}
      size="xl"
      aria-labelledby="contained-modal-title-vright"
      righted
    >
      <div className="price-modal">
        <Modal.Header closeButton className="order-modal">
          <Modal.Title id="contained-modal-title-vright">
            Pricing Table
          </Modal.Title>
        </Modal.Header>

        <Modal.Body className="order-modal">
          {/* {JSON.stringify(props)} */}
          {transformedData?.map((cartData, idx) => {
            const calculateTableTotal = () => {
              // Total for main food item: (Exec. Price + VAT + SD) * Quantity
              const mainItemTotal =
                [
                  cartData.basePrice,
                  cartData.vatInAmount,
                  cartData.sdInAmount,
                ].reduce((a, b) => a + b, 0) * cartData.foodItemQuantity;

              const optionTotal = cartData.addOns
                ?.flatMap((addon) =>
                  addon.options?.map(
                    (option) =>
                      [
                        option.optionBasePrice,
                        option.optionVatAmount,
                        option.optionSdAmount,
                      ].reduce((a, b) => a + b, 0) * option.optionQuantity
                  )
                )
                .reduce((a, b) => a + b, 0);

              const subOptionTotal = cartData.addOns
                ?.flatMap((addon) =>
                  addon.options?.flatMap((option) =>
                    option.subOptions?.map((subOption) =>
                      [
                        subOption.subOptionBasePrice,
                        subOption.subOptionVatAmount,
                        subOption.subOptionSdAmount,
                      ].reduce((a, b) => a + b, 0)
                    )
                  )
                )
                .reduce((a, b) => a + b, 0);

              const addonsTotal = cartData.addOns
                ?.map(
                  (option) =>
                    [
                      option.addOnBasePrice,
                      option.addOnSdAmount,
                      option.addOnVatAmount,
                    ].reduce((a, b) => a + b, 0) * option.addOnQuantity
                )
                .reduce((a, b) => a + b, 0);
              // Return final total for the table
              return mainItemTotal + optionTotal + subOptionTotal + addonsTotal - cartData.credit;
            };

            const tableTotal = calculateTableTotal();

            return (
              <div className="mb-5" key={idx}>
                <TableContainer component={Paper} className="table-container">
                  <Table
                    sx={{ minWidth: 950 }}
                    size="small"
                    aria-label="a dense table"
                  >
                    <TableHead>
                      {restaurantName && restaurantName[idx] && (
                        <h5 style={{ marginLeft: '5px', color: 'gray' }}>
                          {restaurantName[idx]}
                        </h5>
                      )}
                      <TableRow>

                        <TableCell></TableCell>
                        <TableCell align="right">Excl. Price</TableCell>
                        <TableCell align="right">VAT %</TableCell>
                        <TableCell align="right">VAT</TableCell>
                        <TableCell align="right">SD %</TableCell>
                        <TableCell align="right">SD</TableCell>
                        <TableCell align="right">VAT % on SD</TableCell>
                        <TableCell align="right">VAT on SD</TableCell>
                        <TableCell align="right">Inc. SD</TableCell>
                        <TableCell align="right">Credit</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      <TableRow
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell align="left">
                          {cartData.foodItemName} * {cartData.foodItemQuantity}
                        </TableCell>
                        <TableCell align="right">
                          £{cartData.basePrice.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {getPercentageOrAmount(
                            cartData.basePrice,
                            cartData.vatInAmount,
                            "percentage"
                          )}
                          %
                        </TableCell>
                        <TableCell align="right">
                          £{cartData.vatInAmount.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {getPercentageOrAmount(
                            cartData.basePrice,
                            cartData.sdInAmount,
                            "percentage"
                          )}
                          %
                        </TableCell>
                        <TableCell align="right">
                          £{cartData.sdInAmount.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {cartData.vatPercentOnSd}%
                        </TableCell>
                        <TableCell align="right">
                          {" "}
                          £{cartData.vatOnSd.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          £{cartData.inclusiveSD.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          -£{cartData.credit}
                        </TableCell>
                        <TableCell align="right">
                          £
                          {(
                            [
                              cartData.basePrice,
                              cartData.vatInAmount,
                              cartData.sdInAmount,
                            ].reduce((a, b) => a + b, 0) *
                            cartData.foodItemQuantity -
                            cartData.credit
                          ).toFixed(2)}
                        </TableCell>
                      </TableRow>
                      {/* addons list */}
                      {cartData.addOns?.map((addon, index) => (
                        <>
                          <TableRow
                            key={index}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell align="right">
                              +{addon.addOnName} * 1
                            </TableCell>
                            <TableCell align="right">
                              £{addon.addOnBasePrice.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              {getPercentageOrAmount(
                                addon.addOnBasePrice,
                                addon.addOnVatAmount,
                                "percentage"
                              )}
                              %
                            </TableCell>
                            <TableCell align="right">
                              £{addon.addOnVatAmount.toFixed(2)}
                            </TableCell>
                            <TableCell align="right">
                              {getPercentageOrAmount(
                                addon.addOnBasePrice,
                                addon.addOnSdAmount,
                                "percentage"
                              )}
                              %
                            </TableCell>
                            <TableCell align="right">
                              £{addon.addOnSdAmount.toFixed(2)}
                            </TableCell>
                            {/* [hardCodded] */}
                            <TableCell align="right">0%</TableCell>
                            <TableCell align="right">0%</TableCell>
                            <TableCell align="right">0%</TableCell>
                            {/* End [hardCodded] */}
                            <TableCell align="right">
                              £
                              {(
                                [
                                  addon.addOnBasePrice,
                                  addon.addOnVatAmount,
                                  addon.addOnSdAmount,
                                ].reduce((a, b) => a + b, 0) * 1
                              ).toFixed(2)}
                            </TableCell>
                          </TableRow>
                        </>
                      ))}
                      {/* Option List */}
                      {cartData.addOns?.map((addon) =>
                        addon.options?.map((option, index) => (
                          <>
                            <TableRow
                              key={index}
                              sx={{
                                "&:last-child td, &:last-child th": {
                                  border: 0,
                                },
                              }}
                            >
                              <TableCell align="right">
                                +{option.optionName} * {option.optionQuantity}
                              </TableCell>
                              <TableCell align="right">
                                £{option.optionBasePrice.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {getPercentageOrAmount(
                                  option.optionBasePrice,
                                  option.optionVatAmount,
                                  "percentage"
                                )}
                                %
                              </TableCell>
                              <TableCell align="right">
                                £{option.optionVatAmount.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {getPercentageOrAmount(
                                  option.optionBasePrice,
                                  option.optionSdAmount,
                                  "percentage"
                                )}
                                %
                              </TableCell>
                              <TableCell align="right">
                                £{option.optionSdAmount.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {option.optionVatPercentOnSd}%
                              </TableCell>
                              <TableCell align="right">
                                £{option.optionVatOnSd.toFixed(2)}
                              </TableCell>
                              <TableCell align="right">
                                {option.optionInclusiveSD}
                              </TableCell>
                              <TableCell align="right">
                                £
                                {(
                                  [
                                    option.optionBasePrice,
                                    option.optionVatAmount,
                                    option.optionSdAmount,
                                  ].reduce((a, b) => a + b, 0) *
                                  option.optionQuantity
                                ).toFixed(2)}
                              </TableCell>
                            </TableRow>
                            {/* SubOptionNames */}
                            {option?.subOptions.map((subOption, i) => (
                              <TableRow
                                key={i}
                                sx={{
                                  "&:last-child td, &:last-child th": {
                                    border: 0,
                                  },
                                }}
                              >
                                <TableCell align="right">
                                  + {subOption.subOptionName}
                                </TableCell>
                                <TableCell align="right">
                                  £{subOption.subOptionBasePrice.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                  {/* {getPercentageOrAmount(
                                    subOption.subOptionBasePrice,
                                    subOption.subOptionVatAmount,
                                    "percentage"
                                  )} */}
                                  0%
                                </TableCell>
                                <TableCell align="right">
                                  £{subOption.subOptionVatAmount.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                  {/* {getPercentageOrAmount(
                                    subOption.subOptionBasePrice,
                                    subOption.subOptionSdAmount,
                                    "percentage"
                                  )} */}
                                  0%
                                </TableCell>
                                <TableCell align="right">
                                  £{subOption.subOptionSdAmount.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                  {subOption.subOptionVatPercentOnSd}%
                                </TableCell>
                                <TableCell align="right">
                                  £{subOption.subOptionVatOnSd.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                  £{subOption.subOptionInclusiveSD.toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                  £
                                  {[
                                    subOption.subOptionBasePrice,
                                    subOption.subOptionVatAmount,
                                    subOption.subOptionSdAmount,
                                  ]
                                    .reduce((a, b) => a + b, 0)
                                    .toFixed(2)}
                                </TableCell>
                              </TableRow>
                            ))}
                          </>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                <div className="d-flex justify-content-between mt-2 mx-3">
                  <div className="fw-bold">Total</div>
                  <div className="fw-bold">£{tableTotal.toFixed(2)}</div>
                </div>
              </div>
            );
          })}

          <hr />

          <div className="d-flex justify-content-between ms-3 orderDetails">
            <h5>Excl. Total Item Price</h5>
            <h5>£{exclusivePrice.toFixed(2)}</h5>
          </div>
          <div className="d-flex justify-content-between ms-3 orderDetails">
            <h5>Sub Total</h5>
            <h5>£{subTotal.toFixed(2)}</h5>
          </div>
          <div className="d-flex justify-content-between ms-3 orderDetails">
            <h5>Total SD</h5>
            <h5>£{totalSD.toFixed(2)}</h5>
          </div>
          <div className="d-flex justify-content-between ms-3 orderDetails">
            <h5>Price Inc. SD</h5>
            <h5>£{(subTotal + totalSD).toFixed(2)}</h5>
          </div>
          {/* Service charge display
              - From Checkout (multiple restaurants): show a single aggregated Service Charge, no toggles
              - From OrderDetails (single restaurant): show row with toggle for that restaurant
          */}
          {isMultiRestaurant ? (
            <div className="d-flex justify-content-between ms-3 orderDetails">
              <div className="d-flex align-items-center">
                <h5 className="mb-0 me-2">Service Charge</h5>
              </div>
              <h5>£{calCulateServiceCharge().toFixed(2)}</h5>
            </div>
          ) : (
            userCartData?.map((cart) => {
              const rid = cart.cartRestaurantId;
              const enabled = (serviceChargeEnabledByRestaurant && serviceChargeEnabledByRestaurant[rid] !== undefined)
                ? serviceChargeEnabledByRestaurant[rid]
                : (localIsServiceChargeEnabledByRestaurant[rid] ?? true);
              const calcSCForRestaurant = () => {
                if (!enabled) return 0;
                let sub = 0;
                cart.subOrders.forEach((s) => {
                  if (s.orderTypeName !== 'In-Store') return;
                  s.itemDetails.forEach((i) => {
                    sub += i.basePrice * i.foodItemQuantity;
                    i.addOns.forEach((a) => {
                      sub += a.addOnBasePrice * a.addOnQuantity;
                      a.options.forEach((o) => {
                        sub += o.optionBasePrice * o.optionQuantity;
                        o.subOptions.forEach((ss) => {
                          const qty = ss.subOptionQuantity ?? 1;
                          sub += ss.subOptionBasePrice * qty;
                        });
                      });
                    });
                  });
                });
                return (cart.serviceCharge / 100) * sub;
              };
              return (
                <div key={rid} className="d-flex justify-content-between ms-3 orderDetails">
                  <div className="d-flex align-items-center">
                    <h5 className="mb-0 me-2">Service Charge ({cart.serviceCharge || 0}%)</h5>
                    <Tooltip title={cart.isServiceChargeCom === true ? "Service charge is mandatory" : "Toggle service charge"} arrow>
                      <span>
                        <AntSwitch
                          checked={enabled}
                          disabled={cart.isServiceChargeCom === true}
                          onChange={() => handleServiceChargeToggle(rid)}
                          inputProps={{ "aria-label": "service charge toggle" }}
                        />
                      </span>
                    </Tooltip>
                  </div>
                  <h5>£{calcSCForRestaurant().toFixed(2)}</h5>
                </div>
              );
            })
          )}

          {calCulateServiceCharge() > 0 && (
            <div className="d-flex justify-content-between ms-3 orderDetails">
              <h5>Service Charge VAT (20%)</h5>
              <h5>£{serviceChargeVat.toFixed(2)}</h5>
            </div>
          )}

          <div className="d-flex justify-content-between ms-3 orderDetails">
            <h5>Delivery Charge</h5>
            <h5>{deliveryCharge ? <>£{deliveryCharge}</> : <>£0.00</>}</h5>
          </div>

          <div className="d-flex justify-content-between ms-3 orderDetails">
            <h5>Total VAT</h5>
            <h5>£{totalVAT.toFixed(2)}</h5>
          </div>

          <div className="d-flex justify-content-between ms-3 orderDetails">
            <h5>Total Credits</h5>
            <h5>-£{calculateTotalCredit()}</h5>
          </div>

          <div className="d-flex justify-content-around mt-5">
            <div className="fw-bold fs-4">Total Price</div>
            <div className="fw-bold fs-4">£{calculateTotal()}</div>
          </div>
        </Modal.Body>

        <Modal.Footer className="order-modal">
          <Button onClick={props.onHide}>Close</Button>
        </Modal.Footer>
      </div>
    </Modal>
  );
};

export default OrderDetailsModal;
